import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Stack, CircularProgress, Paper,
  Chip, Collapse, Tooltip, IconButton,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { getReleaseDashboard } from '../../api/client'
import type { ReleaseBranchStatus, HotfixCommit } from '../../api/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso?: string): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return `${Math.floor(d / 30)}mo ago`
}

function shortSha(sha: string): string {
  return sha.length > 7 ? sha.slice(0, 7) : sha
}

// ── Hotfix commit list (expandable) ──────────────────────────────────────────

function HotfixList({ hotfixes }: { hotfixes: HotfixCommit[] }) {
  return (
    <Stack gap={0.5} sx={{ mt: 1 }}>
      {hotfixes.map((c, i) => (
        <Box key={c.sha || i} sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5,
          bgcolor: '#fef9ec', border: '1px solid #fde68a',
          borderRadius: 1, px: 1.5, py: 0.75,
        }}>
          <Box sx={{
            fontFamily: 'monospace', fontSize: 11, color: '#92400e',
            bgcolor: '#fef3c7', px: 0.75, py: 0.25, borderRadius: 0.5,
            flexShrink: 0, mt: 0.1,
          }}>
            {shortSha(c.sha)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontSize: 12, display: 'block', color: '#78350f' }}>
              {c.message}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
              {c.author ?? 'unknown'} · {relativeTime(c.date)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  )
}

// ── Single release branch card ────────────────────────────────────────────────

function BranchCard({
  branch, developBranch,
}: {
  branch: ReleaseBranchStatus
  developBranch: string
}) {
  const [expanded, setExpanded] = useState(branch.aheadCount > 0)
  const hasHotfixes = branch.aheadCount > 0
  const hotfixColor = hasHotfixes ? '#d97706' : '#10b981'

  return (
    <Paper variant="outlined" sx={{
      overflow: 'hidden',
      borderColor: hasHotfixes ? '#fcd34d' : '#e2e8f0',
      borderWidth: hasHotfixes ? 1.5 : 1,
    }}>
      {/* Row header */}
      <Box
        onClick={() => setExpanded(e => !e)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2,
          cursor: 'pointer',
          bgcolor: hasHotfixes ? '#fffbeb' : 'white',
          '&:hover': { bgcolor: hasHotfixes ? '#fef9ec' : '#f8fafc' },
        }}
      >
        {/* Branch icon + health */}
        <Box sx={{ flexShrink: 0 }}>
          {hasHotfixes
            ? <WarningAmberIcon sx={{ fontSize: 18, color: '#d97706' }} />
            : <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#10b981' }} />
          }
        </Box>

        {/* Branch name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Box
              component={branch.branchUrl ? 'a' : 'span'}
              href={branch.branchUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                fontFamily: 'monospace', fontSize: 13, fontWeight: 600,
                color: '#0f172a', bgcolor: '#f1f5f9',
                px: 1, py: 0.25, borderRadius: 0.75,
                textDecoration: 'none',
                '&:hover': branch.branchUrl ? { bgcolor: '#e2e8f0', color: '#1d4ed8' } : {},
              }}
            >
              {branch.name}
            </Box>

            {hasHotfixes ? (
              <Chip
                label={`${branch.aheadCount} hotfix${branch.aheadCount !== 1 ? 'es' : ''} not in ${developBranch}`}
                size="small"
                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 11, height: 22 }}
              />
            ) : (
              <Chip
                label={`in sync with ${developBranch}`}
                size="small"
                sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600, fontSize: 11, height: 22 }}
              />
            )}
          </Stack>

          {branch.lastCommitMessage && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, mt: 0.25, display: 'block' }}>
              Last: {branch.lastCommitMessage}
              {branch.lastCommitAuthor ? ` — ${branch.lastCommitAuthor}` : ''}
              {branch.lastCommitDate ? ` · ${relativeTime(branch.lastCommitDate)}` : ''}
            </Typography>
          )}
        </Box>

        {/* Behind / ahead counters */}
        <Stack direction="row" gap={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {branch.behindCount > 0 && (
            <Tooltip title={`${branch.behindCount} commits in ${developBranch} not yet in this release`}>
              <Chip
                label={`${branch.behindCount} behind`}
                size="small"
                sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: 11, height: 22 }}
              />
            </Tooltip>
          )}
          {hasHotfixes && (
            <Tooltip title={`${branch.aheadCount} commits in this branch not yet merged back to ${developBranch}`}>
              <Chip
                label={`${branch.aheadCount} ahead`}
                size="small"
                sx={{ bgcolor: '#fef3c7', color: hotfixColor, fontWeight: 600, fontSize: 11, height: 22 }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* Expand toggle */}
        <Box sx={{ color: '#94a3b8', fontSize: 14, flexShrink: 0, userSelect: 'none' }}>
          {expanded ? '▲' : '▼'}
        </Box>
      </Box>

      {/* Hotfix detail */}
      <Collapse in={expanded && hasHotfixes}>
        <Box sx={{
          px: 3, pb: 2, pt: 1,
          borderTop: '1px solid #fde68a',
          bgcolor: '#fffbeb',
        }}>
          <Typography variant="caption" sx={{
            fontSize: 11, fontWeight: 600, color: '#92400e',
            textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1,
          }}>
            Hotfixes — need to be merged back to {developBranch}
          </Typography>
          <HotfixList hotfixes={branch.hotfixes} />
          {branch.aheadCount > branch.hotfixes.length && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11, mt: 1, display: 'block' }}>
              + {branch.aheadCount - branch.hotfixes.length} more commit{branch.aheadCount - branch.hotfixes.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

// ── develop branch header ─────────────────────────────────────────────────────

function DevelopHeader({ name }: { name: string }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2,
      px: 3, py: 2,
      bgcolor: '#eff6ff', border: '1.5px solid #bfdbfe',
      borderRadius: 2,
    }}>
      <Box sx={{
        width: 10, height: 10, borderRadius: '50%',
        bgcolor: '#3b82f6', flexShrink: 0,
        boxShadow: '0 0 0 3px #bfdbfe',
      }} />
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box sx={{
            fontFamily: 'monospace', fontSize: 14, fontWeight: 700,
            color: '#1e40af', bgcolor: '#dbeafe',
            px: 1, py: 0.25, borderRadius: 0.75,
          }}>
            {name}
          </Box>
          <Chip
            label="integration branch"
            size="small"
            sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: 11, height: 22 }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, mt: 0.25, display: 'block' }}>
          All work merges here — hotfixes in release branches should also be merged back
        </Typography>
      </Box>
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ReleasesPage() {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['release-dashboard'],
    queryFn: getReleaseDashboard,
    refetchInterval: 5 * 60_000,
    staleTime: 2 * 60_000,
  })

  const dashboard = data?.data
  const branches  = dashboard?.branches ?? []

  const totalHotfixes   = branches.reduce((s, b) => s + b.aheadCount, 0)
  const branchesWithFix = branches.filter(b => b.aheadCount > 0).length

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h5" fontWeight={600}>Release Branch State</Typography>
          {dashboard && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
              {dashboard.owner}/{dashboard.repository} ·{' '}
              fetched {relativeTime(dashboard.fetchedAt)}
            </Typography>
          )}
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          {!isLoading && branches.length > 0 && (
            <>
              {branchesWithFix > 0 && (
                <Chip
                  label={`${totalHotfixes} hotfix${totalHotfixes !== 1 ? 'es' : ''} unmerged`}
                  size="small"
                  sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
                />
              )}
              <Chip
                label={`${branches.length} release branch${branches.length !== 1 ? 'es' : ''}`}
                size="small"
                sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }}
              />
            </>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}
              sx={{ fontSize: 16, color: '#64748b' }}>
              {isFetching ? <CircularProgress size={16} /> : '↺'}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Comparing release branches against {dashboard?.developBranch ?? 'develop'}…
          </Typography>
        </Box>
      )}

      {/* Empty / not configured */}
      {!isLoading && branches.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {dashboard
              ? 'No release branches found matching the configured prefix.'
              : 'Release dashboard not configured. Set DevOps__ReleaseDashboard__Owner and DevOps__ReleaseDashboard__Repository in the deployment.'}
          </Typography>
        </Paper>
      )}

      {/* develop branch header */}
      {!isLoading && dashboard && branches.length > 0 && (
        <DevelopHeader name={dashboard.developBranch} />
      )}

      {/* Connector line */}
      {!isLoading && branches.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2.5, pl: 2.5, borderLeft: '2px dashed #e2e8f0' }}>
          {branches.map(b => (
            <BranchCard key={b.name} branch={b} developBranch={dashboard?.developBranch ?? 'develop'} />
          ))}
        </Box>
      )}
    </Box>
  )
}
